import { html, hydrate, node } from 'wirejs-dom/v2';
import { Article } from '../../../../../layouts/index.js';
import { CodeBlock } from '../../../../../components/code-block.js';
import { worker } from 'worker-2025-07-06';

const title = 'Type-Safe RPC-like Web Workers';
const description = `How to create end-to-end, type-safe RPC-like Web Workers in TypeScript.`;
const tags = ['javascript', 'typescript', 'web workers', 'web development', 'npm', 'neat tricks', 'productivity', 'package.json'];
const published = new Date('2025-07-07 9:00 PM');

const content = () => html`<div id='app'>

	<p>Calling code across layers shouldn't require a ton of boilerplate. Crafting complex incantations to "call a function" <i>just because it lives "somewhere else"</i> is &mdash; in my opinion &mdash; <i>pretty silly</i>. For reasons I don't understand, we have a tendency to tolerate multi-step tutorials that instruct us to write dozens of lines of incantation code to ... uhh ... "call a function". But, some ecosystems already perform magic for you when you make service calls. <span class='muted'>(I.e., call functions that live "somewhere else.")</span> For example, when you use a <a href='https://en.wikipedia.org/wiki/SOAP'><code>SOAP</code></a> service in the <a href='https://en.wikipedia.org/wiki/.NET_Framework'><code>.NET</code></a> ecosystem, you pretty much just import a <a href='https://en.wikipedia.org/wiki/Web_Services_Description_Language'><code>WSDL</code></a> and then <i>call the service as though it were any other local code.</i></p>

	<p>Even back in my PHP days <span class='muted'>(over a decade ago)</span>, I had created a similar layer for myself by emitting the glue directly from my backend &mdash; a less feature-full and distinctly more "hacky" solution than <code>.NET's</code> strongly typed code generation. But, the magic was similar. My build system made the calls effectively transparent. My front-end JavaScript invoked backend PHP methods as thought they were just local functions.</p>

	<p>Reflecting on magic like this, it can tempting to think that it's <i>too much</i> magic. Or to think that perhaps it "hides too many details I want control over." There are concerns that warrant hesitation, some cause to consider what knobs you may need to tinker with. I get it. But, here's a gentle reminder <span class='muted'>(if only to my future self)</span>: Function calling magic is a completely essential part of writing <i>completely local</i> code too.</p>

	<p>If you don't already see the point, <b>riddle me this!</b> <i>When is the last time you disavowed your language's <a href='https://en.wikipedia.org/wiki/Calling_convention'>function calling mechanism</a> in favor of a hand-rolled call stack and a pile of <code>GOTO</code>s?</i> <span class='muted'>(Simply using a stack in your code probably doesn't count. In most languages, your stack manipulation depends on the language's builtin function calling mechanism.)</span></p>
	
	<p>So, when it comes to <a href='https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Using_web_workers'>Web Workers</a>, it can see how it makes sense for the primitives to be a little complicated. What make <i>far less</i> sense is why seemingly intelligent folks like myself tend to operate for so long as though "that's just it's done." How do smart people become so accustomed to repeating the same incantation over and over, hand-casting types on either end of message handlers, continually hoping the contracts don't drift on either end, and so on ...</p>

	<p>In our laziness, we tend to do a lot of extra work. And it's generally more error-prone work at that.</p>
	
	<p>Thankfully, I've been working largely in the TypeScript ecosystem over the past few years, and having created similar tricks for service calls using the <code>npm</code> lifecycle and some <code>exports</code> trickery, I've finally made some time to tackle Web Workers. I did look briefly at some existing tooling, but nothing immediately spoke to me. Nothing quite at the level of magic I want. So, I'm rolling my own. <span class='muted'>(Also, sometimes it's just fun to dive in and hand-roll a thing.)</span></p>

	<h4>So, let's look at a sample worker function.</h4>

	<p>The worker just counts to number <span class='muted'>(provided by the caller)</span> and reports progress to a given callback if given.</p>

	${CodeBlock({
		language: 'typescript',
		code: /* typescript */ `
			// workers/2025-07-06/src/index.ts
			import { SingleWorker } from 'wirejs-web-worker';

			export const worker = SingleWorker({
				async count(
					upTo: number,
					options?: { tick?: (pct: number) => void }
				) {
					let lastUpdate = new Date();
					options?.tick?.(0);
					let c = 0;
					for (let i = 0; i < upTo; i++) {
						let current = new Date();
						if (current.getTime() - lastUpdate.getTime() > 50) {
							options?.tick?.(i / upTo);
							lastUpdate = current;
						}
						c++;
					}
					options?.tick?.(1);
					return c;
				}
			});
		`
	})}

	<p>It's a little more complicated of a function than is needed to demonstrate the point. But, I want to be clear here that even callback functions can be marshalled across the wire in a sensible way.</p>

	<h4>From the calling side...</h4>

	<p>I can import the worker from the worker package as though it were any other object. <span class='muted'>(More on that in a moment.)</span> And, I can call methods on it like normal methods.</p>

	${CodeBlock({
		language: 'typescript',
		code: `
			import { worker } from 'worker-2025-07-06';

			const start = () => worker.count(123_000_000, {
				tick: pct => {
					if (pct < 1) {
						self.data.output = html\`<span>\${(pct*100).toFixed(1)}% done ...</span>\`;
					}
				}
			}).then(x => self.data.output = html\`
				<span>Counted to \${x.toLocaleString()}. <button
					onclick=\${start}>Start over.</button></span>
			\`);

			start();
		`,
	})}

	<p>And the result is this:</p>

	<pre class='code-block'>${node('output', html`<span>...</span>` as HTMLElement)}</pre>

	<h4>So, what's the trick?</h4>

	<p>It really boils down to two basic things. Firstly, we create a subpackage and control the outsider's view of the package via the <code>exports</code> property. My somewhat naive solution's <code>package.json</code> tells the IDE and TypeScript to look at the code itself when determining types. <span class='muted'>(This could also point at a generated <code>d.ts</code> file. But, as I already said, I'm lazy.)</span> And, it tells the bundler to look at a build artifact.</p>
	
	${CodeBlock({
		language: 'json',
		code: /* json */ `
		// package.json
		{
			"exports": {
				"types": "./index.ts",
				"default": "./dist/index.js"
			}
		}
		`
	})}

	<p>So far, this is actually very normal TypeScript/JavaScript stuff. Almost any time you import code from somewhere else in the TS/JS ecosystem, you're pointing the IDE and TypeScript at one export and the bundler at another. But, that brings us to the second tricky bit.</p>

	<p>I'm not <i>just</i> creating a bundle during my build step of this subpackage. Instead, my builder for the Web Worker subpackage does three things:</p>

	<ol>
		<li>Bundles the worker code.</li>
		<li>Injects the bundled worker code into a Web Worker wrapper.</li>
		<li>Bundles the wrapper <span class='muted'>(Which, as you'll recall, includes the worker bundle.)</span></li>
	</ol>

	<p>So, when the client calls function, it's actually calling the wrapper. The wrapper spins up a Web Worker using the embedded worker bundle and manages the marshalling of messages to and from the worker through a <a href='https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Proxy'>Proxy</a>.</p>

	<p>And, here's the <b>best</b> part: You can try my solution out for yourself! <span class='muted'>(Probably. I didn't actually test it in <i>your</i> environment.)</span> It's <a href='https://www.npmjs.com/package/wirejs-web-worker'><code>wirejs-web-worker</code></a> in NPM.</p>

	<p><i>Alternatively</i>, have some fun and roll your own. 🍻</p>

	<p>Or not at all. But, if you were waiting for some encouragement to go make something marginally easier for yourself with code &mdash; if only for the fun and learning, here it is:</p>

	<p><b>Go make something marginally easier for yourself with code.</b> <span class='muted'>(If only for the fun and learning.)</span></p>

</div>`.onadd(self => {
	const start = () => worker.count(123_000_000, {
		tick: pct => {
			if (pct < 1) {
				self.data.output = html`<span>${(pct*100).toFixed(1)}% done ...</span>`;
			}
		}
	}).then(x => self.data.output = html`
		<span>Counted to ${x.toLocaleString()}. <button
			onclick=${start}>Start over.</button></span>
	`);
	start();
});

export const generate = async () => Article({
	title,
	content: content(),
	description,
	tags,
	published
});

export function onload() {
	hydrate('app', content as any);
}